/**
 * App-wide "are you sure?" — the replacement for the browser's `window.confirm()`.
 *
 * Works like sonner's `toast`: any code calls `confirmDialog({...})` and awaits
 * a boolean, and the single `<ConfirmDialogHost />` mounted in App.tsx draws the
 * shadcn AlertDialog. That keeps a call site to one line —
 *
 *   if (!(await confirmDialog({ title: 'Delete phase?', destructive: true }))) return
 *
 * — instead of each page carrying its own open-state and dialog markup.
 *
 * A confirmation that has to stay open while a request runs and show the
 * server's refusal (DeleteBudgetDialog, ArchiveDepartmentDialog) is a different
 * thing and keeps its own component.
 */
import { useEffect, useRef, useState } from 'react'
import {
  AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'

export interface ConfirmOptions {
  title: string
  description?: string
  /** Label of the button that goes ahead. Default "Confirm". */
  confirmLabel?: string
  /** Label of the button that backs out. Default "Cancel". */
  cancelLabel?: string
  /** Red confirm button, for deletes and other things that lose work. */
  destructive?: boolean
}

interface Pending extends ConfirmOptions {
  resolve: (confirmed: boolean) => void
}

let show: ((pending: Pending) => void) | null = null

export function confirmDialog(options: ConfirmOptions): Promise<boolean> {
  return new Promise((resolve) => {
    // No host mounted (a test, or a page outside App): say no rather than hang.
    if (!show) { resolve(false); return }
    show({ ...options, resolve })
  })
}

export function ConfirmDialogHost() {
  // `open` is separate from `pending` so the words stay on screen while the
  // dialog fades out, instead of the box emptying mid-animation.
  const [pending, setPending] = useState<Pending | null>(null)
  const [open, setOpen] = useState(false)
  // Whatever had focus when the question was asked. There is no Radix Trigger
  // to return to, so without this focus falls to <body> after "Cancel" and a
  // keyboard user loses their place in the form underneath.
  const openerRef = useRef<HTMLElement | null>(null)

  useEffect(() => {
    show = (next) => {
      openerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null
      // A second question while one is open answers the first with "no".
      setPending((previous) => { previous?.resolve(false); return next })
      setOpen(true)
    }
    return () => { show = null }
  }, [])

  function answer(confirmed: boolean) {
    pending?.resolve(confirmed)
    setOpen(false)
  }

  return (
    <AlertDialog open={open} onOpenChange={(next) => { if (!next) answer(false) }}>
      <AlertDialogContent
        className="w-[calc(100%-2rem)] text-ink-text sm:max-w-md"
        // A title-only question has no description to point at; say so, or
        // Radix warns about a missing one.
        {...(pending?.description ? {} : { 'aria-describedby': undefined })}
        onCloseAutoFocus={(event) => {
          event.preventDefault()
          if (openerRef.current?.isConnected) openerRef.current.focus()
        }}
      >
        <AlertDialogHeader>
          <AlertDialogTitle className="text-ink-text">{pending?.title}</AlertDialogTitle>
          {pending?.description && (
            <AlertDialogDescription className="pt-1 text-left leading-relaxed text-ink-muted">
              {pending.description}
            </AlertDialogDescription>
          )}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="rounded-full border-ink-border bg-ink-module text-ink-text hover:bg-ink-hover">
            {pending?.cancelLabel ?? 'Cancel'}
          </AlertDialogCancel>
          <Button
            variant={pending?.destructive ? 'destructive' : 'default'}
            className="rounded-full"
            onClick={() => answer(true)}
          >
            {pending?.confirmLabel ?? 'Confirm'}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
